import { useEffect, useState } from 'react';

import moment from 'moment';
import {
  ArrowSquareOut,
  CheckCircle,
  Coins,
  CreditCard,
  Timer,
  User,
  WarningCircle,
  XCircle,
} from 'phosphor-react';

import {
  DillTransaction,
  DillTransactionStatus,
  PaymentPlanFailedChargeRecord,
  PaymentPlanMissedPaymentRecord,
  PaymentPlanPaymentRecord,
  formatMoney,
} from '@dill/dill-shared';

import { COLORS } from '../../../utils/colors';
import { useAppDispatch } from '../../../redux/store';
import { getTransactionById } from '../../../redux/services/paymentsService';
import { AppPaidInvoicesModal } from '../AppPaidInvoicesModal/AppPaidInvoicesModal';

type HistoryRow =
  | { kind: 'PAID'; date: Date | string; record: PaymentPlanPaymentRecord }
  | { kind: 'MISSED'; date: Date | string; record: PaymentPlanMissedPaymentRecord }
  | { kind: 'FAILED'; date: Date | string; record: PaymentPlanFailedChargeRecord };

export const PAID_SOURCE_DISPLAY: Record<
  PaymentPlanPaymentRecord['source'],
  { label: string; className: string; icon: JSX.Element }
> = {
  BUYER_PAYMENT: {
    label: 'Paid',
    className: 'bg-blue-50 text-blue-700',
    icon: <Coins size={11} color={COLORS.PRIMARY_500} />,
  },
  AUTO_CHARGE: {
    label: 'Auto-charged',
    className: 'bg-green-50 text-green-700',
    icon: <CreditCard size={11} color={COLORS.SUCCESS_700} />,
  },
  MANUAL: {
    label: 'Recorded manually',
    className: 'bg-gray-100 text-gray-600',
    icon: <User size={11} color={COLORS.GREY_500} />,
  },
};

/**
 * Mirrors the underlying DillTransaction's `transactionStatus` - kept up to date after the fact by
 * `updateDillTransaction`'s status-sync hook, so this reflects whether a "Paid"/"Auto-charged" row
 * has actually settled (processed/completed), is still in flight (waiting/pending/initiated), or
 * ultimately failed/was reversed (failed/cancelled/refunded) at the provider.
 */
const TRANSACTION_STATUS_DISPLAY: Record<
  DillTransactionStatus,
  { label: string; className: string; icon: JSX.Element }
> = {
  waiting: {
    label: 'Waiting',
    className: 'bg-amber-50 text-amber-700',
    icon: <Timer size={11} color={COLORS.WARNING_500} />,
  },
  pending: {
    label: 'Pending',
    className: 'bg-amber-50 text-amber-700',
    icon: <Timer size={11} color={COLORS.WARNING_500} />,
  },
  initiated: {
    label: 'Initiated',
    className: 'bg-amber-50 text-amber-700',
    icon: <Timer size={11} color={COLORS.WARNING_500} />,
  },
  processed: {
    label: 'Processed',
    className: 'bg-green-50 text-green-700',
    icon: <CheckCircle size={11} color={COLORS.SUCCESS_700} />,
  },
  completed: {
    label: 'Completed',
    className: 'bg-green-50 text-green-700',
    icon: <CheckCircle size={11} color={COLORS.SUCCESS_700} />,
  },
  failed: {
    label: 'Failed',
    className: 'bg-red-50 text-red-700',
    icon: <XCircle size={11} color={COLORS.ERROR_500} weight="fill" />,
  },
  cancelled: {
    label: 'Cancelled',
    className: 'bg-red-50 text-red-700',
    icon: <XCircle size={11} color={COLORS.ERROR_500} weight="fill" />,
  },
  refunded: {
    label: 'Refunded',
    className: 'bg-gray-100 text-gray-600',
    icon: <User size={11} color={COLORS.GREY_500} />,
  },
};

export const PaymentPlanHistoryList = ({
  paymentHistory,
  missedPayments,
  failedCharges,
  accountType = 'buyer',
  maxHeightClassName = 'max-h-80',
}: {
  paymentHistory?: PaymentPlanPaymentRecord[] | null;
  missedPayments?: PaymentPlanMissedPaymentRecord[] | null;
  failedCharges?: PaymentPlanFailedChargeRecord[] | null;
  /** Who's viewing - determines which transaction-detail permissions/actions the modal offers. */
  accountType?: 'supplier' | 'buyer';
  /** Tailwind max-height class for the scrollable row list - overridable for an embedded, more
   * space-constrained placement (e.g. inside a card) vs. the modal's roomier default. */
  maxHeightClassName?: string;
}) => {
  const dispatch = useAppDispatch();
  const [selectedTransaction, setSelectedTransaction] = useState<DillTransaction | null>(null);
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  // Live fallback for rows whose `status` was never stamped (recorded before that field existed)
  // or hasn't synced yet - looked up on demand so the badge still reflects the real transaction
  // instead of just showing nothing.
  const [liveStatusByTransactionId, setLiveStatusByTransactionId] = useState<
    Record<string, DillTransactionStatus | null>
  >({});

  const rows: HistoryRow[] = [
    ...(paymentHistory ?? []).map(
      (record): HistoryRow => ({ kind: 'PAID', date: record.date, record })
    ),
    ...(missedPayments ?? []).map(
      (record): HistoryRow => ({ kind: 'MISSED', date: record.dueDate, record })
    ),
    ...(failedCharges ?? []).map(
      (record): HistoryRow => ({ kind: 'FAILED', date: record.attemptedAt, record })
    ),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  useEffect(() => {
    const idsNeedingLiveStatus = Array.from(
      new Set(
        (paymentHistory ?? [])
          .filter((record) => !record.status && record.transactionId)
          .map((record) => record.transactionId as string)
      )
    ).filter((transactionId) => !(transactionId in liveStatusByTransactionId));

    if (idsNeedingLiveStatus.length === 0) return;

    idsNeedingLiveStatus.forEach(async (transactionId) => {
      const result = await dispatch(getTransactionById({ transactionId }));
      const payload =
        result.type === 'payments/getTransactionById/fulfilled'
          ? (result.payload as { data?: DillTransaction } | null)
          : null;
      setLiveStatusByTransactionId((prev) => ({
        ...prev,
        [transactionId]: payload?.data?.transactionStatus ?? null,
      }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentHistory]);

  const handleViewTransaction = async (transactionId: string) => {
    const result = await dispatch(getTransactionById({ transactionId }));
    if (result.type === 'payments/getTransactionById/fulfilled') {
      const payload = result.payload as { data?: DillTransaction } | null;
      if (payload?.data) {
        setSelectedTransaction(payload.data);
        setIsTransactionModalOpen(true);
      }
    }
  };

  if (rows.length === 0) {
    return <p className="text-xs text-gray-400 text-center py-8">No payments recorded yet.</p>;
  }

  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden">
      <div className="grid grid-cols-5 px-4 py-2.5 text-2xs font-semibold text-gray-500 bg-gray-50">
        <span>Date</span>
        <span>Amount</span>
        <span>Source</span>
        <span>Status</span>
        <span></span>
      </div>
      <div className={`${maxHeightClassName} overflow-y-auto`}>
        {rows.map((row, i) => {
          const effectiveStatus =
            row.kind === 'PAID'
              ? row.record.status ??
                (row.record.transactionId
                  ? liveStatusByTransactionId[row.record.transactionId]
                  : null)
              : null;

          return (
            <div
              key={`${row.date}-${i}`}
              className="grid grid-cols-5 px-4 py-2.5 text-xs items-center border-t border-gray-100">
              <span>{moment(row.date).format('MMM DD, YYYY')}</span>
              <span className="font-medium">
                {row.kind === 'MISSED' ? '—' : formatMoney(row.record.amount, 2)}
              </span>
              <span>
                {row.kind === 'PAID' && (
                  <span
                    className={`inline-flex items-center gap-1 w-fit px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                      PAID_SOURCE_DISPLAY[row.record.source].className
                    }`}>
                    {PAID_SOURCE_DISPLAY[row.record.source].icon}
                    {PAID_SOURCE_DISPLAY[row.record.source].label}
                  </span>
                )}
                {row.kind !== 'PAID' && '—'}
              </span>
              <div className="min-w-0">
                {row.kind === 'PAID' && effectiveStatus && (
                  <span
                    className={`inline-flex items-center gap-1 w-fit px-2 py-0.5 rounded-full text-[11px] font-semibold ${TRANSACTION_STATUS_DISPLAY[effectiveStatus].className}`}>
                    {TRANSACTION_STATUS_DISPLAY[effectiveStatus].icon}
                    {TRANSACTION_STATUS_DISPLAY[effectiveStatus].label}
                  </span>
                )}
                {row.kind === 'MISSED' && (
                  <span className="inline-flex items-center gap-1 w-fit px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-700">
                    <WarningCircle size={11} color={COLORS.ERROR_500} />
                    Missed
                  </span>
                )}
                {row.kind === 'FAILED' && (
                  <>
                    <span className="inline-flex items-center gap-1 w-fit px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-700">
                      <XCircle size={11} color={COLORS.ERROR_500} weight="fill" />
                      Failed
                    </span>
                    <p className="text-2xs text-gray-400 truncate mt-0.5" title={row.record.reason}>
                      {row.record.reason}
                    </p>
                  </>
                )}
              </div>
              <span>
                {row.kind === 'PAID' && row.record.transactionId && (
                  <button
                    type="button"
                    onClick={() => handleViewTransaction(row.record.transactionId as string)}
                    className="inline-flex items-center gap-1 text-2xs font-medium text-blue-600 hover:text-blue-700">
                    View
                    <ArrowSquareOut size={11} />
                  </button>
                )}
              </span>
            </div>
          );
        })}
      </div>

      <AppPaidInvoicesModal
        open={isTransactionModalOpen}
        accountType={accountType}
        selectedTransaction={selectedTransaction}
        handleClose={() => {
          setIsTransactionModalOpen(false);
          setSelectedTransaction(null);
        }}
        onViewMainPayment={handleViewTransaction}
      />
    </div>
  );
};

export default PaymentPlanHistoryList;
